/**
 * Secondary Navigation Slider
 * Creates a mobile-only slider for the secondary navigation
 * that mimics the behavior of the announcement slider
 */

if (!customElements.get('secondary-nav-slider')) {
  class SecondaryNavSlider extends HTMLElement {
    constructor() {
      super();
      this.container = this.querySelector('.secondary-nav');
      this.items = this.querySelectorAll('.secondary-nav > li');
      this.isPaused = false;
      
      // Fixed slide delay with explicit fallback to ensure correct timing
      this.slideDelay = parseInt(this.dataset.slideDelay) || 2000;
      
      // Log for debugging
      console.log('Secondary nav slider delay:', this.slideDelay);

      // Only initialize if we have items and we're on mobile
      if (this.items.length > 1) {
        this.createControls();
        this.bindEvents();
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.checkViewportHandler);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.prevSlideHandler);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.nextSlideHandler);
    }

    createControls() {
      // Create the controls container
      this.controls = document.createElement('div');
      this.controls.className = 'secondary-nav-controls';
      
      // Create left control with updated SVG
      this.prevBtn = document.createElement('button');
      this.prevBtn.className = 'secondary-nav-control secondary-nav-control--prev';
      this.prevBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M7.63578 10.1358L13.1008 15.6008L12.3643 16.3374L6.16264 10.1358L12.3643 3.93413L13.1008 4.6707L7.63578 10.1358Z" fill="black"/></svg>';
      this.prevBtn.setAttribute('aria-label', 'Previous item');
      this.prevSlideHandler = this.prevSlide.bind(this);
      this.prevBtn.addEventListener('click', this.prevSlideHandler);
      
      // Create right control with updated SVG
      this.nextBtn = document.createElement('button');
      this.nextBtn.className = 'secondary-nav-control secondary-nav-control--next';
      this.nextBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M12.3642 10.1358L6.89917 15.6008L7.63574 16.3374L13.8374 10.1358L7.63574 3.93413L6.89917 4.6707L12.3642 10.1358Z" fill="black"/></svg>';
      this.nextBtn.setAttribute('aria-label', 'Next item');
      this.nextSlideHandler = this.nextSlide.bind(this);
      this.nextBtn.addEventListener('click', this.nextSlideHandler);
      
      // Add controls to the controls container
      this.controls.appendChild(this.prevBtn);
      this.controls.appendChild(this.nextBtn);
      
      // Insert controls into the DOM
      this.appendChild(this.controls);
    }

    bindEvents() {
      // Pause slider on mouse hover
      this.container.addEventListener('mouseenter', this.pauseSlider.bind(this));
      this.container.addEventListener('mouseleave', this.resumeSlider.bind(this));
      
      // Check viewport size changes
      this.checkViewportHandler = this.checkViewport.bind(this);
      window.addEventListener('resize', this.checkViewportHandler);
    }

    init() {
      // Setup initial state
      this.setupSlider();
      this.checkViewport();
      
      // Start the rotation
      this.startRotation();
    }

    setupSlider() {
      // Make container scrollable
      this.container.classList.add('secondary-nav--slider');
      
      // Mark first item as visible
      this.items[0].classList.add('is-visible');
      
      // Hide all other items initially
      for (let i = 1; i < this.items.length; i++) {
        this.items[i].classList.remove('is-visible');
      }
    }

    startRotation() {
      const nextSlide = () => {
        // Only run slider if on mobile and not paused
        if (!this.isPaused && window.innerWidth < 769 && this.isInViewport()) {
          this.showNextSlide();
        }
        
        // Continue the rotation with the fixed delay
        setTimeout(nextSlide, this.slideDelay);
      };
      
      // Start the rotation with the fixed delay
      setTimeout(nextSlide, this.slideDelay);
    }

    showNextSlide() {
      const currentSlideIndex = this.getCurrentSlideIndex();
      const nextSlideIndex = (currentSlideIndex + 1) % this.items.length;
      
      // Hide current slide
      this.items[currentSlideIndex].classList.remove('is-visible');
      
      // Show next slide
      this.items[nextSlideIndex].classList.add('is-visible');
      
      // Scroll to it (for accessibility)
      this.scrollToSlide(nextSlideIndex);
    }

    showPrevSlide() {
      const currentSlideIndex = this.getCurrentSlideIndex();
      const prevSlideIndex = (currentSlideIndex - 1 + this.items.length) % this.items.length;
      
      // Hide current slide
      this.items[currentSlideIndex].classList.remove('is-visible');
      
      // Show previous slide
      this.items[prevSlideIndex].classList.add('is-visible');
      
      // Scroll to it (for accessibility)
      this.scrollToSlide(prevSlideIndex);
    }

    nextSlide(event) {
      event.preventDefault();
      this.isPaused = true;
      this.showNextSlide();
      
      // Resume after a longer pause to give time to read
      clearTimeout(this.resumeTimeout);
      this.resumeTimeout = setTimeout(() => {
        this.isPaused = false;
      }, 5000);
    }

    prevSlide(event) {
      event.preventDefault();
      this.isPaused = true;
      this.showPrevSlide();
      
      // Resume after a longer pause to give time to read
      clearTimeout(this.resumeTimeout);
      this.resumeTimeout = setTimeout(() => {
        this.isPaused = false;
      }, 5000);
    }

    getCurrentSlideIndex() {
      for (let i = 0; i < this.items.length; i++) {
        if (this.items[i].classList.contains('is-visible')) {
          return i;
        }
      }
      return 0;
    }

    scrollToSlide(index) {
      if (this.items[index]) {
        const slide = this.items[index];
        if (document.dir === 'rtl') {
          this.container.scrollTo({
            left: -Math.abs(slide.offsetLeft),
            behavior: 'smooth'
          });
        } else {
          this.container.scrollTo({
            left: slide.offsetLeft,
            behavior: 'smooth'
          });
        }
      }
    }

    checkViewport() {
      if (window.innerWidth < 769) {
        // Mobile view - enable slider
        this.container.classList.add('secondary-nav--active');
      } else {
        // Desktop view - disable slider
        this.container.classList.remove('secondary-nav--active');
        
        // Make all items visible on desktop
        this.items.forEach(item => {
          item.classList.add('is-visible');
        });
      }
    }

    pauseSlider() {
      this.isPaused = true;
    }

    resumeSlider() {
      this.isPaused = false;
    }

    isInViewport() {
      const rect = this.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    }
  }

  customElements.define('secondary-nav-slider', SecondaryNavSlider);
}
